import type { ComponentProps } from 'react';

import type { Dayjs } from 'dayjs';
import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  DateTimePicker,
  type DateTimePickerProps,
} from '../../date-pickers/date-time-picker/date-time-picker';

interface Transform {
  input?: (value: unknown) => ComponentProps<typeof DateTimePicker>['value'];
  output?: (value: ComponentProps<typeof DateTimePicker>['value']) => unknown;
}

export type ControlledDateTimePickerProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  transform?: Transform;
} & Omit<DateTimePickerProps, 'value' | 'onChange'>;

export function ControlledDateTimePicker<T extends FieldValues>({
  control,
  defaultValue,
  disabled,
  errorMessage,
  helperText,
  label,
  minDate,
  maxDate,
  name,
  onError,
  pickerProps,
  placeholder,
  required,
  textFieldProps,
  transform,
  clearable,
  ...rest
}: ControlledDateTimePickerProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      {...rest}
      render={({ field, fieldState }) => (
        <DateTimePicker
          {...field}
          disabled={disabled}
          errorMessage={errorMessage ?? fieldState.error?.message}
          helperText={helperText}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          name={name}
          onChange={(newValue: Dayjs | null) =>
            field.onChange(
              transform?.output ? transform.output(newValue) : newValue,
            )
          }
          onError={onError}
          placeholder={placeholder}
          pickerProps={pickerProps}
          required={required}
          textFieldProps={textFieldProps}
          value={transform?.input ? transform.input(field.value) : field.value}
          clearable={clearable}
        />
      )}
    />
  );
}
